// Wraps the Osteotomy-Angle annotated frame in the unified report chrome.
//
// The annotated canvas is the output of AnnotationCanvas.flatten() — a clean
// image-plus-lines composite with no labels or watermark. This module adds
// the CADtomie header / footer and a prominent angle readout next to the
// frame so the export looks like part of the same product family as the
// torsion report.

import { buildReport } from './reportFrame';

export interface AngleReportInput {
  annotated: HTMLCanvasElement;
  /** The measured angle in degrees. */
  angleDeg: number;
  /** Free vs. shared-vertex mode — surfaced as a subtitle in the header. */
  mode: 'free' | 'shared';
  timestamp: number;
}

const ANGLE_BLUE = '#7cc4ff';

export function buildAngleReport(input: AngleReportInput): HTMLCanvasElement {
  const variant = input.mode === 'shared' ? 'Shared vertex' : 'Free lines';
  const result = `Angle: ${input.angleDeg.toFixed(1)}°`;

  return buildReport(
    {
      module: 'angle',
      variant,
      result,
      timestamp: input.timestamp,
    },
    (ctx, rect) => {
      // Layout: image takes ~70% of the body width, readout panel ~30%.
      const GAP = 32;
      const readoutW = 360;
      const imgMaxW = rect.w - readoutW - GAP;

      const srcAR = input.annotated.height / input.annotated.width;
      const imgW = imgMaxW;
      const imgH = imgW * srcAR;

      // Image with rounded corners
      const imgX = rect.x;
      const imgY = rect.y;
      ctx.save();
      roundedRectPath(ctx, imgX, imgY, imgW, imgH, 16);
      ctx.clip();
      ctx.drawImage(input.annotated, imgX, imgY, imgW, imgH);
      ctx.restore();
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.lineWidth = 1;
      roundedRectPath(ctx, imgX + 0.5, imgY + 0.5, imgW - 1, imgH - 1, 16);
      ctx.stroke();

      // Readout panel beside the image
      const panelX = imgX + imgW + GAP;
      const panelY = imgY;
      const panelW = readoutW;
      const panelH = imgH;
      ctx.fillStyle = '#161618';
      roundedRectPath(ctx, panelX, panelY, panelW, panelH, 16);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 1;
      roundedRectPath(ctx, panelX + 0.5, panelY + 0.5, panelW - 1, panelH - 1, 16);
      ctx.stroke();

      // Panel content
      const panelPad = 32;
      let py = panelY + panelPad + 4;

      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.font =
        '600 14px -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif';
      ctx.fillText('MEASURED ANGLE', panelX + panelPad, py);
      py += 28;

      ctx.fillStyle = '#ffffff';
      ctx.font =
        'bold 96px -apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif';
      ctx.textBaseline = 'top';
      ctx.fillText(`${input.angleDeg.toFixed(1)}°`, panelX + panelPad, py);
      py += 110;

      ctx.fillStyle = ANGLE_BLUE;
      ctx.font = '600 14px -apple-system, system-ui, sans-serif';
      ctx.fillText(variant.toUpperCase() + ' MODE', panelX + panelPad, py);
      py += 22;

      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.font = '500 13px -apple-system, system-ui, sans-serif';
      const desc =
        input.mode === 'shared'
          ? 'Two arms sharing one vertex — opening angle 0°–180°.'
          : 'Two independent lines — acute angle 0°–90° between them.';
      wrapText(ctx, desc, panelX + panelPad, py, panelW - panelPad * 2, 18);

      return imgH;
    },
  );
}

/* ─── small canvas helpers used here only ─── */

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  lineH: number,
) {
  const words = text.split(/\s+/);
  let line = '';
  let yy = y;
  for (const w of words) {
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, yy);
      yy += lineH;
      line = w;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, yy);
}
